import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {

  // --- Scroll lock state (fixes iPhone background page scrolling while drawer is open) ---
  #scrollLocked = false;
  #scrollY = 0;

  #isIOS =
    typeof navigator !== 'undefined' &&
    /iPad|iPhone|iPod/.test(navigator.userAgent) &&
    !window.MSStream;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);

    // Ensure we always unlock when the dialog closes (ESC, backdrop close, programmatic)
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (dialog) {
      dialog.addEventListener('close', this.#unlockPageScroll);
    }

    // IMPORTANT: bind once in a stable asset (not in an innerHTML-swapped snippet)
    document.documentElement.addEventListener('cart:change', this.#iosNudgeScrollContainer, { passive: true });
    document.documentElement.addEventListener('cart:refresh', this.#iosNudgeScrollContainer, { passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);

    const { dialog } = /** @type {Refs} */ (this.refs);
    if (dialog) {
      dialog.removeEventListener('close', this.#unlockPageScroll);
    }

    document.documentElement.removeEventListener('cart:change', this.#iosNudgeScrollContainer);
    document.documentElement.removeEventListener('cart:refresh', this.#iosNudgeScrollContainer);

    // Safety: never leave the page locked if element is removed
    this.#unlockPageScroll();
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
      this.#lockPageScroll();
    }
  };

  open() {
    this.showDialog();
    this.#lockPageScroll();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');

      cta?.addEventListener(
        'click',
        () => {
          this.closeDialog();
          this.#unlockPageScroll();
        },
        { once: true }
      );
    });
  }

  close() {
    this.closeDialog();
    this.#unlockPageScroll();
  }

  // ---------- iOS-safe scroll lock helpers ----------
  #lockPageScroll = () => {
    if (this.#scrollLocked) return;

    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    document.documentElement.setAttribute('data-cart-drawer-open', 'true');

    this.#scrollY = window.scrollY || window.pageYOffset || 0;
    this.#scrollLocked = true;

    const body = document.body;
    body.style.width = '100%';

    if (this.#isIOS) {
      body.style.position = 'fixed';
      body.style.top = `-${this.#scrollY}px`;
      body.style.left = '0';
      body.style.right = '0';
    } else {
      body.style.overflow = 'hidden';
    }

    const content =
      dialog.querySelector('.cart-drawer__items') ||
      dialog.querySelector('.cart-drawer__content') ||
      dialog.querySelector('.drawer__content') ||
      dialog;

    if (content) {
      content.style.overflowY = 'auto';
      content.style.webkitOverflowScrolling = 'touch';
    }
  };

  #unlockPageScroll = () => {
    if (!this.#scrollLocked) return;

    document.documentElement.removeAttribute('data-cart-drawer-open');

    const body = document.body;
    body.style.overflow = '';
    body.style.position = '';
    body.style.top = '';
    body.style.left = '';
    body.style.right = '';
    body.style.width = '';

    window.scrollTo(0, this.#scrollY);

    this.#scrollLocked = false;
    this.#scrollY = 0;
  };

  // --- iOS “scroll context nudge” (prevents Safari from losing the drawer scroll after innerHTML swaps) ---
  #iosNudgeScrollContainer = () => {
    if (!this.#isIOS) return;

    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Only do this if the drawer is actually open
    // (dialog.open is true for <dialog> elements when displayed)
    if (!dialog.open) return;

    // Wait for DOM to finish painting after cart swap
    setTimeout(() => {
      const drawerContent = dialog.querySelector('.cart-drawer__items') || dialog.querySelector('.cart-drawer__content, .drawer__content');
      if (!drawerContent) return;

      // Toggle overflow to force iOS Safari to recalculate the scrollable area
      const prev = drawerContent.style.overflowY;
      drawerContent.style.overflowY = 'hidden';
      // layout reflow
      void drawerContent.offsetHeight;
      drawerContent.style.overflowY = prev || 'auto';
    }, 120);
  };

  // Items scroll independently; checkout stays visible below.
  #updateStickyState() {
    this.refs.dialog?.setAttribute('cart-summary-sticky', 'true');
  }

}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}